import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { Alert, Button, FieldError, FieldRow, Input, Label, sentinel } from '@sentinel/ui';
import { totpCodeSchema, type TotpCodeInput } from '../schema';

export interface TotpStepProps {
  secret: string;
  provisioningUri: string;
  onSubmit: (code: string) => Promise<void> | void;
  /** Inline (under the form) error such as "code_invalid". */
  error?: string | null;
  submitting?: boolean;
}

async function clearClipboard() {
  try {
    // Use a space (not '') because some browsers no-op on empty strings.
    await navigator.clipboard.writeText(' ');
  } catch {
    // ignore
  }
}

export function TotpStep({
  secret,
  provisioningUri,
  onSubmit,
  error,
  submitting,
}: TotpStepProps) {
  const { t } = useTranslation('mfa');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrFailed, setQrFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const form = useForm<TotpCodeInput>({ resolver: zodResolver(totpCodeSchema) });

  useEffect(() => {
    let cancelled = false;
    setQrFailed(false);
    setQrDataUrl(null);
    QRCode.toDataURL(provisioningUri, { errorCorrectionLevel: 'M', margin: 1, width: 220 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [provisioningUri]);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        void clearClipboard();
      }, 2000);
    } catch {
      // ignore — clipboard may be unavailable in some contexts
    }
  }

  // Clear clipboard on unmount so the TOTP secret doesn't linger.
  useEffect(() => {
    return () => {
      void clearClipboard();
    };
  }, []);

  async function handleSubmit(values: TotpCodeInput) {
    await onSubmit(values.code.trim());
    void clearClipboard();
  }

  const codeErrorRaw = form.formState.errors.code?.message;
  const codeError = codeErrorRaw === 'code_invalid' ? t('code_invalid') : codeErrorRaw;

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <p>{t('totp_intro')}</p>

      <div style={{ display: 'grid', placeItems: 'center', gap: '0.5rem' }}>
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt={t('totp_qr_alt')}
            width={220}
            height={220}
            style={{ display: 'block' }}
          />
        ) : qrFailed ? (
          <Alert type="warning" role="alert">
            {t('totp_qr_unavailable')}
          </Alert>
        ) : (
          <div style={{ width: 220, height: 220 }} aria-hidden="true" />
        )}
      </div>

      <FieldRow>
        <Label htmlFor="mfa-totp-secret">{t('totp_secret_label')}</Label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <code
            id="mfa-totp-secret"
            data-testid="mfa-totp-secret"
            style={{
              flex: 1,
              fontFamily: sentinel.fontMono,
              fontSize: '1rem',
              padding: '0.625rem 0.875rem',
              background: sentinel.surfaceOverlay,
              border: `1px solid ${sentinel.borderDefault}`,
              color: sentinel.textBody,
              borderRadius: 6,
              wordBreak: 'break-all',
            }}
          >
            {secret}
          </code>
          <Button type="button" variant="secondary" onClick={onCopy} aria-live="polite">
            {copied ? t('totp_copied') : t('totp_copy')}
          </Button>
        </div>
      </FieldRow>

      {error ? (
        <Alert type="error" role="alert">
          {error}
        </Alert>
      ) : null}

      <form onSubmit={form.handleSubmit(handleSubmit)} style={{ display: 'grid', gap: '0.75rem' }}>
        <FieldRow>
          <Label htmlFor="mfa-totp-code">{t('totp_code_label')}</Label>
          <Input
            id="mfa-totp-code"
            data-testid="mfa-totp-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder={t('totp_code_placeholder')}
            aria-invalid={form.formState.errors.code ? 'true' : undefined}
            {...form.register('code')}
          />
          <FieldError message={codeError} />
        </FieldRow>
        <Button
          type="submit"
          data-testid="mfa-totp-submit"
          disabled={Boolean(submitting) || form.formState.isSubmitting}
        >
          {t('totp_submit')}
        </Button>
      </form>
    </div>
  );
}
